import { motion } from 'motion/react';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { elementsTransition } from '@/utils/gamePacks';
import SwitchToDesktopImage from '@/assets/images/switch_to_desktop.png';
import LeftFixedShape from '../Host/Setup/BackgroundElements/LeftFixedShape';
import BottomLineElement from '../Host/Setup/BackgroundElements/BottomLineElement';
import CenterFixedShape from '../Host/Setup/BackgroundElements/CenterFixedShape';
import LogoIcon from '../Icons/LogoIcon';
import { CheckIcon } from '../Icons/CommonIcons';

const MobileDeviceBlock = (): ReactElement => {
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    document.documentElement.classList.add('mobile-device-block');
    document.body.classList.add('mobile-device-block');

    return () => {
      document.documentElement.classList.remove('mobile-device-block');
      document.body.classList.remove('mobile-device-block');

      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = null;
      }
    };
  }, []);

  const handleCopyLink = () => {
    const link = window ? window.location.href : '';
    if (!link) return;
    navigator?.clipboard?.writeText(link);

    setCopied(true);

    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }

    copyTimeoutRef.current = window.setTimeout(() => {
      setCopied(false);
      copyTimeoutRef.current = null;
    }, 2000);
  };

  return (
    <motion.div
      className="relative min-h-screen w-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="h-full w-full absolute z-0 overflow-y-auto bg-secondary-clementine-100">
        <div className="py-20 h-short:py-12 mx-20 border-b border-neutral-200 flex items-center justify-center">
          <LogoIcon className="w-80" />
        </div>

        <div className="flex justify-center mt-90 h-short:mt-24 px-20">
          <div className="relative w-full max-w-520">
            <CenterFixedShape
              className="absolute z-0 top-260 h-short:top-140 left-1/2 -translate-x-1/2"
              initial={{ scale: 0.5 }}
              animate={{ scale: 1 }}
              transition={elementsTransition}
            />
            <div className="relative z-10 bg-white rounded-xs shadow-xs px-24 h-short:px-20 py-32 h-short:py-20 text-center">
              <img
                src={SwitchToDesktopImage}
                className="mx-auto h-auto w-185 h-short:w-140"
                alt="Monitor and phone illustration"
              />
              <h2 className="text-h2 h-short:text-h3 mt-24 h-short:mt-16">
                Scribl Requires a Bigger Screen
              </h2>
              <h3 className="text-h3 h-short:text-body mt-12 h-short:mt-8">
                Please switch to a desktop or laptop to manage games
              </h3>
              <p className="text-body h-short:text-body-sm mt-16 h-short:mt-12">
                Scribl works best on desktop for creating and hosting games. Players can join and
                draw from the phone once the game starts.
              </p>
              <div className="relative">
                <button
                  className="btn btn-primary my-24 h-short:my-16 w-full active:scale-97"
                  onClick={handleCopyLink}
                >
                  Copy Link To Use on Desktop
                </button>
                {copied && (
                  <motion.div
                    className="absolute left-0 right-0 top-78 flex items-center justify-center gap-3 px-12 py-6 text-body-sm text-utility-success-500"
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                  >
                    <CheckIcon className="w-18 h-18" />
                    Link copied!
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </div>

        <LeftFixedShape
          className="absolute top-135 left-0 origin-left"
          initial={{ scale: 0.5 }}
          animate={{ scale: 1 }}
          transition={elementsTransition}
        />

        <BottomLineElement
          className="absolute bottom-0 left-1/2 -translate-x-1/2 origin-bottom"
          initial={{ scale: 0.5 }}
          animate={{ scale: 1 }}
          transition={elementsTransition}
        />
      </div>
    </motion.div>
  );
};

export default MobileDeviceBlock;
